/**
 * HTTP client: axios instance with request/response interceptors.
 *
 * - Request interceptor: sets baseURL via getBaseUrl(), injects session-id header
 *   from the active virtual session ID (set after bootstrap).
 * - Response interceptor: 401/403 trigger alert events; optional service_class callbacks for CRUD intercept.
 * - Cancel token helpers: createCancelToken, getCancelToken, cancelToken for request cancellation.
 */

import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { getBaseUrl, isCallable, isFalsy } from 'shared/utils/common';
import { AUTH_EVENTS } from 'features/session/auth-events';

let _activeVirtualSessionId: string | null = null;

export function setActiveVirtualSessionId(id: string | null): void {
  _activeVirtualSessionId = id;
}

export function clearActiveVirtualSessionId(): void {
  _activeVirtualSessionId = null;
}

export function getActiveVirtualSessionId(): string | null {
  return _activeVirtualSessionId;
}
import Emitter from 'shared/events/emitter';
import EVENT_NAMES from 'shared/constants/events';

/** Axios request config with optional service_class for intercept callbacks */
interface RequestConfigWithServiceClass extends InternalAxiosRequestConfig {
  service_class?: {
    onCreateInterceptSuccess?: (response: unknown) => void;
    onUpdateInterceptSuccess?: (response: unknown) => void;
    onDeleteInterceptSuccess?: (response: unknown) => void;
    onCreateInterceptFail?: (response: unknown) => void;
    onUpdateInterceptFail?: (response: unknown) => void;
    onDeleteInterceptFail?: (response: unknown) => void;
  };
}

function hasSessionHeader(config: RequestConfigWithServiceClass | undefined): boolean {
  const headers = config?.headers;
  if (!headers) return false;
  return Boolean(headers['session-id'] || headers['Session-Id']);
}

function notifySessionInvalid(): void {
  clearActiveVirtualSessionId();
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_EVENTS.SESSION_INVALID, { detail: { reason: 'request_failed' } }));
}

function alertUnauthenticated(): void {
  Emitter.emit(EVENT_NAMES.ALERT.ERROR_DISPLAY, {
    title: 'Unauthenticated',
    message: 'Sorry, you are not authenticated to this',
    options: { timer: 3000 },
  });
}

function alertUnauthorized(): void {
  Emitter.emit(EVENT_NAMES.ALERT.ERROR_DISPLAY, {
    title: 'Unauthorized',
    message: 'Sorry, you are not authorized to this',
    options: { timer: 3000 },
  });
}

const AxiosInstance = axios.create({
  withCredentials: true,
}) as AxiosInstance & {
  CancelToken: typeof axios.CancelToken;
  isCancel: (value: unknown) => boolean;
  cancel_tokens: Record<string, ReturnType<typeof axios.CancelToken.source>>;
  createCancelToken: () => ReturnType<typeof axios.CancelToken.source>;
  getCancelToken: (token_key: string) => ReturnType<typeof axios.CancelToken.source> | null;
  cancelToken: (token_key: string) => void;
  getSportsbookUrl: Promise<string>;
};

(AxiosInstance as unknown as Record<string, unknown>).CancelToken = axios.CancelToken;
(AxiosInstance as unknown as Record<string, unknown>).isCancel = axios.isCancel;
AxiosInstance.defaults.timeout = 10000000;
AxiosInstance.defaults.headers.common['Content-Type'] = 'application/json';
AxiosInstance.getSportsbookUrl = getBaseUrl();
AxiosInstance.cancel_tokens = {};

AxiosInstance.createCancelToken = function (): ReturnType<typeof axios.CancelToken.source> {
  const source = axios.CancelToken.source();
  (source as unknown as Record<string, unknown>).throwIfRequested = source.token.throwIfRequested.bind(source.token);
  (source as unknown as Record<string, unknown>).promise = source.token.promise;
  return source;
};

AxiosInstance.getCancelToken = function (token_key: string) {
  return this.cancel_tokens[token_key] ?? null;
};

AxiosInstance.cancelToken = function (token_key: string): void {
  const source = this.getCancelToken(token_key);
  if (source) source.cancel();
};

// Request interceptor: base URL and session-id header
AxiosInstance.interceptors.request.use(
  async (config) => {
    config.baseURL = await getBaseUrl();
    const sid = _activeVirtualSessionId;
    if (sid) {
      config.headers['session-id'] = sid;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: success/error handling and 401/403 alerts
AxiosInstance.interceptors.response.use(
  (response) => {
    const config = response.config as RequestConfigWithServiceClass;
    const service_class = config.service_class;

    if (response.status === 200) {
      switch (response.config.method?.toLowerCase()) {
        case 'post':
          if (service_class && isCallable(service_class, 'onCreateInterceptSuccess')) {
            service_class.onCreateInterceptSuccess!(response);
          }
          break;
        case 'put':
          if (service_class && isCallable(service_class, 'onUpdateInterceptSuccess')) {
            service_class.onUpdateInterceptSuccess!(response);
          }
          break;
        case 'delete':
          if (service_class && isCallable(service_class, 'onDeleteInterceptSuccess')) {
            service_class.onDeleteInterceptSuccess!(response);
          }
          break;
        default:
          return response;
      }
    }
    if (response.status === 401) {
      alertUnauthenticated();
    }
    if (response.status === 403) {
      alertUnauthorized();
    }
    return response;
  },
  (error) => {
    const { response } = error;
    if (response) {
      const config = response.config as RequestConfigWithServiceClass;
      const service_class = config?.service_class;
      if (!isFalsy(response) && !isFalsy(response.status) && response.status !== undefined) {
        if (response.status === 401) {
          if (hasSessionHeader(config) || getActiveVirtualSessionId()) {
            notifySessionInvalid();
          }
          alertUnauthenticated();
        }
        if (response.status === 403) {
          if (hasSessionHeader(config) || getActiveVirtualSessionId()) {
            notifySessionInvalid();
          }
          alertUnauthorized();
        }
        if (response.status === 422) {
          switch (response.config?.method?.toLowerCase()) {
            case 'post':
              if (service_class && isCallable(service_class, 'onCreateInterceptFail')) {
                service_class.onCreateInterceptFail!(response);
              }
              break;
            case 'put':
              if (service_class && isCallable(service_class, 'onUpdateInterceptFail')) {
                service_class.onUpdateInterceptFail!(response);
              }
              break;
            case 'delete':
              if (service_class && isCallable(service_class, 'onDeleteInterceptFail')) {
                service_class.onDeleteInterceptFail!(response);
              }
              break;
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

export default AxiosInstance;
